import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Checkbox, Select } from "semantic-ui-react";
import {
  configName,
  configStartWith,
  getAllConfigs,
  getConfigDetails,
} from "../../api/configApi";
import {
  getBulkDropdownData,
  getDropdownData,
  getTextByValueAndKey,
} from "../../api/keyvalue";
import { emptyValidator } from "../../validators";
import Grid from "../Grid";
import LoadingSpinner from "../LoadingSpinner";
import Button from "../input/Button";
import Dropdown from "../input/Dropdown";
import ButtonRenderer from "../renderer/ButtonRenderer";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import { configDataValidation } from "../validations/stakeholderCreation";
import { AUTH_TYPES, FORMS, inputFieldProfile, MODULES } from "../../const";
import { isNumber } from "../../helper";
import Input from "../input/Input";
import { hasMultiple } from "../../common/logic";
import { toast } from "react-toastify";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.STAKEHOLDER.CONFIG.form;

const StakeholderConfig = forwardRef(
  ({ dropdown, isLoading, currentValues, editMode }, ref) => {
    const [stkConfigData, setStkConfigData] = useState({
      scnCode: "",
      scnStakeholder: "",
      scnStakeholderName: "",
      scnConfig: "",
      scnConfigId: "",
      scnConfigName: "",
      scnBoolean: "",
      scnText: "",
      scnNumber: 0,
      scnList: [],
      scnUom: 0,
      scnUomName: "",
      scnDescription: "",
      scnStatus: 2,
      scnActive: 1,
      scnActiveValue: "",
      scnfStatusValue: "",
      conBoolean: false,
      conNumber: -1,
      conText: "-1",
      conDropdown: "-1",
      conBooleanLabel: "Is Active",
      conTextLabel: "Text",
      conNumberLabel: "Number",
      conUomLabel: "UOM",
      conDropdownLabel: "Dropdown",
      conMultiDropdown: false,
      conUom: -1,
    });

    const [buttonClicked, setButtonClicked] = useState(false);
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [configCount, setConfigCount] = useState(2);
    const [currentErrors, setCurrentErrors] = useState({});
    const [deletedList, setDeletedList] = useState([]);

    const [stkConfigList, setStkConfigList] = useState([]);
    const [currentStkConfig, setCurrentStkConfig] = useState({});

    const [statusOpt, setStatusOpt] = useState([]);
    const [activeOpt, setActiveOpt] = useState([]);
    const [uomOpt, setUomOpt] = useState([]);
    const [stkConfigOpt, setStkConfigOpt] = useState([]);
    const [dropdownOptions, setDropdownOptions] = useState([]);

    const [mFields, setMFields] = useState({});
    const isActiveInactiveVisible = stkConfigData.conBoolean === true;
    const isNumberFieldVisible = stkConfigData.conNumber !== -1;
    const isTextFieldVisible = stkConfigData.conText !== "-1";
    const isDropdownVisible = stkConfigData.conDropdown !== "-1";
    const isMultiDropdown = stkConfigData.conMultiDropdown === true;
    const isUomVisible = stkConfigData.conUom !== -1;

    const [isConfigDataLoading, setIsConfigDataLoading] = useState(false);

    const mandatoryFields = useSelector(
      (state) => state.stakeholder.configurationFormFields,
    );

    useEffect(() => {
      if (Object.entries(mandatoryFields).length <= 0) {
        setMFields({
          scnConfig: {
            field: "scnConfig",
            name: "Configuration",
            required: true,
          },
          scnStatus: {
            field: "scnStatus",
            name: "Status",
            required: true,
          },
          scnActive: {
            field: "scnActive",
            name: "Active",
            required: true,
          },
        });
      } else setMFields(mandatoryFields);
    }, [mandatoryFields]);

    const onAddClick = async () => {
      setIsAddLoading(true);

      let validationState = {};

      for (const [key, value] of Object.entries(mFields)) {
        validationState[key] = {
          validators: [emptyValidator()],
          name: value.name,
        };
      }

      let configErrors = configDataValidation(stkConfigData, validationState);

      if (Object.keys(configErrors).length === 0) {
        const nonUniqueRecord = hasMultiple({
          data: stkConfigList,
          newItem: stkConfigData,
          validator: (config) => config.scnConfig === stkConfigData.scnConfig,
          persistentIdentifier: "scnId",
        });

        if (nonUniqueRecord) {
          toast.warn("Cannot add multiple records with same Configuration", {
            containerId: "Main",
            autoClose: 1000,
          });
          setIsAddLoading(false);
          return;
        }
        const scnUomName = await getTextByValueAndKey(
          "uom",
          stkConfigData.scnUom,
        );
        const scnActiveValue = await getTextByValueAndKey(
          "active",
          stkConfigData.scnActive,
        );
        const scnStatusValue = await getTextByValueAndKey(
          "status",
          stkConfigData.scnStatus,
        );
        let scnConfigName = "";
        if (stkConfigData.scnConfig !== "") {
          scnConfigName = await configName(stkConfigData.scnConfig);
        }

        if (!stkConfigData.scnId) {
          if (!stkConfigData.id) {
            setStkConfigList((prevState) => [
              ...prevState,
              {
                ...stkConfigData,
                id: configCount,
                scnUomName,
                scnActiveValue,
                scnStatusValue,
                scnConfigName,
              },
            ]);
            setConfigCount((prevState) => ++prevState);
          } else {
            setStkConfigList((prevState) =>
              prevState.filter((item) => item.id !== stkConfigData.id),
            );
            setStkConfigList((prevState) => [
              ...prevState,
              {
                ...stkConfigData,
                scnUomName,
                scnActiveValue,
                scnStatusValue,
                scnConfigName,
              },
            ]);
          }
        } else {
          setStkConfigList((prevState) =>
            prevState.filter((item) => item.scnId !== stkConfigData.scnId),
          );
          setStkConfigList((prevState) => [
            ...prevState,
            {
              ...stkConfigData,
              scnUomName,
              scnActiveValue,
              scnStatusValue,
              scnConfigName,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }

      setCurrentErrors(configErrors);
      setIsAddLoading(false);
    };

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            unmodified: [],
            deleted: deletedList,
          };

          stkConfigList.forEach((scn) => {
            if (scn.scnId) {
              if (scn.modified) {
                updateState.modified.push(scn);
              } else {
                updateState.unmodified.push(scn);
              }
            } else {
              updateState.created.push(scn);
            }
          });

          return updateState;
        } else {
          return stkConfigList;
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setStkConfigList([]);
        setCurrentErrors({});
      },
    }));

    useEffect(() => {
      (async () => {
        //new
        const {
          status,
          active,
          uom: uomOpt,
        } = await getBulkDropdownData([
          { key: "status" },
          { key: "active" },
          { key: "uom" },
        ]);

        setStatusOpt(status);
        setActiveOpt(active);
        setUomOpt(uomOpt);

        const configData = await getAllConfigs();
        setStkConfigOpt(configData);
      })();
    }, []);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentStkConf = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentStkConf.push({
              ...currentValue,
              scnConfig: currentValue.scnConfigId,
              id,
            });
            id++;
          }

          setConfigCount(id);
          setStkConfigList(currentStkConf);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (Object.keys(currentStkConfig).length > 0) {
        setStkConfigData({
          ...currentStkConfig,
          scnUomName: "",
          scnActiveValue: "",
          scnStatusValue: "",
          scnConfigName: "",
        });
      }
    }, [editMode, currentStkConfig]);

    useEffect(() => {
      if (buttonClicked) {
        setStkConfigData({
          scnCode: "",
          scnStakeholder: "",
          scnStakeholderName: "",
          scnConfig: "",
          scnConfigId: "",
          scnConfigName: "",
          scnBoolean: "",
          scnText: "",
          scnNumber: 0,
          scnList: [],
          scnUom: "",
          scnUomName: "",
          scnDescription: "",
          scnStatus: 2,
          scnActive: 1,
          scnActiveValue: "",
          scnfStatusValue: "",
        });
        setCurrentStkConfig({});
        setButtonClicked(false);
        setCurrentStkConfig({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      (async () => {
        setIsConfigDataLoading(true);

        if (stkConfigData.scnConfig) {
          const configDetails = await getConfigDetails(stkConfigData.scnConfig);
          if (configDetails) {
            setStkConfigData((prevState) => ({
              ...prevState,
              conBoolean: configDetails.conBoolean,
              conDropdown: configDetails.conDropdown,
              conMultiDropdown: configDetails.conMultiDropdown,
              conUom: configDetails.conUom,
              conText: configDetails.conText,
              conNumber: configDetails.conNumber,
              conBooleanLabel: configDetails.conBooleanLabel,
              conTextLabel: configDetails.conTextLabel,
              conNumberLabel: configDetails.conNumberLabel,
              conUomLabel: configDetails.conUomLabel,
              conDropdownLabel: configDetails.conDropdownLabel,
            }));

            if (
              configDetails.conDropdown !== "-1" &&
              configDetails.conDropdown !== null &&
              configDetails.conDropdown !== undefined
            ) {
              const keyValue = configDetails.conDropdown;
              const [key, group] = keyValue.split("#");
              const hasGroup = group !== undefined;

              console.log(key, group);

              let dropdownData = [];

              if (hasGroup) {
                dropdownData = await getDropdownData(key, group.toString());
              } else {
                dropdownData = await getDropdownData(key);
              }
              setDropdownOptions(dropdownData);
            } else {
              setDropdownOptions([]);
            }
          }
        }

        setIsConfigDataLoading(false);
      })();
    }, [stkConfigData.scnConfig]);

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Stakeholder Config</h4>
              <div className="two fields">
                <Dropdown
                  required={mFields.scnConfig?.required}
                  label="Configuration"
                  options={stkConfigOpt}
                  error={currentErrors.scnConfig}
                  editMode={editMode}
                  module={MODULES.Master}
                  search={true}
                  form={FORM}
                  field="scn_config"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { scnConfig: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  style={{ height: "20px" }}
                  placeholder={"Configuration"}
                  value={stkConfigData.scnConfig}
                  onChange={async (e, data) => {
                    setStkConfigData((prevState) => ({
                      ...prevState,
                      scnConfig: data.value,
                    }));
                  }}
                />
                <Input
                  type="text"
                  placeholder="Description"
                  label="Description"
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="scn_description"
                  value={stkConfigData.scnDescription}
                  onChange={(e) => {
                    setStkConfigData((prevState) => ({
                      ...prevState,
                      scnDescription: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="ui segment">
                <h4 className="ui dividing header">Configuration Settings</h4>
                {stkConfigData.scnConfig ? (
                  <>
                    {!isConfigDataLoading ? (
                      <>
                        <div className="five fields">
                          {isActiveInactiveVisible && (
                            <div
                              className={`field ${currentErrors.scnBoolean ? "error" : ""}`}
                            >
                              <label>
                                {stkConfigData.conBooleanLabel ??
                                  "Active / Inactive"}
                              </label>
                              <Checkbox
                                type="Checkbox"
                                name="primary"
                                checked={stkConfigData.scnBoolean}
                                value={stkConfigData.scnBoolean}
                                disabled={stkConfigData.scnConfig === 14}
                                onChange={(e) => {
                                  setStkConfigData((prevState) => ({
                                    ...prevState,
                                    scnBoolean: !prevState.scnBoolean,
                                  }));
                                }}
                                editMode={editMode}
                                module={MODULES.Master}
                                form={FORM}
                                field="scn_boolean"
                              />
                              <small style={{ color: "#9f3a38" }}>{`${
                                currentErrors.scnBoolean
                                  ? currentErrors.scnBoolean
                                  : ""
                              }`}</small>
                            </div>
                          )}
                          {isDropdownVisible && !isMultiDropdown && (
                            <Dropdown
                              label={
                                stkConfigData.conDropdownLabel ?? "Dropdown"
                              }
                              module={MODULES.Master}
                              form={FORM}
                              placeholder={
                                stkConfigData.conDropdownLabel ?? "Dropdown"
                              }
                              options={dropdownOptions}
                              value={stkConfigData.scnNumber}
                              style={{ height: "5px" }}
                              onChange={(e, data) => {
                                setStkConfigData((prevState) => ({
                                  ...prevState,
                                  scnNumber: data.value,
                                }));
                              }}
                            />
                          )}
                          {isTextFieldVisible && (
                            <Input
                              profile={
                                stkConfigData.conText === "2"
                                  ? inputFieldProfile.DECIMAL
                                  : inputFieldProfile.TEXT
                              }
                              name="text"
                              label={stkConfigData.conTextLabel ?? "Text"}
                              error={currentErrors.scnText}
                              resetError={() =>
                                setCurrentErrors((prevState) => {
                                  const { scnText: _, ...rest } = prevState;
                                  return rest;
                                })
                              }
                              editMode={editMode}
                              module={MODULES.Master}
                              form={FORM}
                              field="scn_text"
                              placeholder={
                                stkConfigData.conText === "2" ? "0.00" : ""
                              }
                              value={
                                stkConfigData.conText === "2"
                                  ? stkConfigData.scnText === null ||
                                    stkConfigData.scnText === "" ||
                                    stkConfigData.scnText === undefined
                                    ? 0.0
                                    : parseFloat(stkConfigData.scnText)
                                  : stkConfigData.scnText
                              }
                              onChange={(e) => {
                                if (stkConfigData.conText === 2) {
                                  const value = e.target.value;
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    value === "" ||
                                    (Number(value) > 0 && !isNaN(value))
                                  ) {
                                    const intValue = parseFloat(value);
                                    setStkConfigData((prevState) => ({
                                      ...prevState,
                                      scnText: isNaN(intValue) ? 0 : intValue,
                                    }));
                                  }
                                } else {
                                  const value = e.target.value;
                                  setStkConfigData((prevState) => ({
                                    ...prevState,
                                    scnText: value,
                                  }));
                                }
                              }}
                            />
                          )}
                          {isNumberFieldVisible && (
                            <Input
                              profile={inputFieldProfile.DECIMAL}
                              name="number"
                              label={stkConfigData.conNumberLabel ?? "Number"}
                              error={currentErrors.scnNumber}
                              resetError={() =>
                                setCurrentErrors((prevState) => {
                                  const { scnNumber: _, ...rest } = prevState;
                                  return rest;
                                })
                              }
                              placeholder="0.00"
                              editMode={editMode}
                              module={MODULES.Master}
                              form={FORM}
                              field="scn_number"
                              value={stkConfigData.scnNumber}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === null ||
                                  value === undefined ||
                                  value === "" ||
                                  (Number(value) > 0 && !isNaN(value))
                                ) {
                                  const intValue = parseFloat(value);
                                  setStkConfigData((prevState) => ({
                                    ...prevState,
                                    scnNumber: isNaN(intValue) ? 0 : intValue,
                                  }));
                                }
                              }}
                            />
                          )}
                          {isUomVisible && (
                            <Dropdown
                              required={mFields.scnUom?.required}
                              label={stkConfigData.conUomLabel ?? "UOM"}
                              error={currentErrors.scnUom}
                              resetError={() =>
                                setCurrentErrors((prevState) => {
                                  const { scnUom: _, ...rest } = prevState;
                                  return rest;
                                })
                              }
                              module={MODULES.Master}
                              form={FORM}
                              placeholder={stkConfigData.conUomLabel ?? "UOM"}
                              options={uomOpt}
                              value={stkConfigData.scnUom}
                              style={{ height: "5px" }}
                              onChange={(e, data) => {
                                setStkConfigData((prevState) => ({
                                  ...prevState,
                                  scnUom: data.value,
                                }));
                              }}
                            />
                          )}
                        </div>
                        {isDropdownVisible && isMultiDropdown && (
                          <Dropdown
                            label={stkConfigData.conDropdownLabel ?? "Dropdown"}
                            module={MODULES.Master}
                            form={FORM}
                            placeholder={
                              stkConfigData.conDropdownLabel ?? "Dropdown"
                            }
                            options={dropdownOptions}
                            multiple={true}
                            value={stkConfigData.scnList}
                            style={{ height: "5px" }}
                            onChange={(e, data) => {
                              setStkConfigData((prevState) => ({
                                ...prevState,
                                scnList: data.value,
                              }));
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <h5>Loading....</h5>
                    )}
                  </>
                ) : (
                  <h5>Please select a config</h5>
                )}
              </div>
              <div className="two fields">
                {/* <div
                  className={`field ${currentErrors.scnActive ? "error" : ""}`}
                >
                  <label>
                    {mFields.scnActive && mFields.scnActive.required && (
                      <label
                        style={{
                          color: "#9f3a38",
                          fontSize: "20px",
                          marginRight: "2px",
                        }}
                      >
                        *
                      </label>
                    )}
                    Active
                  </label>
                  <Select
                    placeholder="Active"
                    options={activeOpt}
                    editMode={editMode}
                    module={MODULES.Master}
                    form={FORM}
                    field="scn_active"
                    needDeletePermission={true}
                    clearable
                    value={stkConfigData.scnActive}
                    style={{ height: "20px" }}
                    onChange={(e, data) => {
                      setStkConfigData((prevState) => ({
                        ...prevState,
                        scnActive: data.value,
                      }));
                    }}
                  />
                  {mFields.scnActive && mFields.scnActive.required && (
                    <small style={{ color: "#9f3a38" }}>{`${
                      currentErrors.scnActive ? currentErrors.scnActive : ""
                    }`}</small>
                  )}
                </div> */}
                <div
                  className={`field ${currentErrors.scnStatus ? "error" : ""}`}
                >
                  <label>
                    {mFields.scnStatus && mFields.scnStatus.required && (
                      <label
                        style={{
                          color: "#9f3a38",
                          fontSize: "20px",
                          marginRight: "2px",
                        }}
                      >
                        *
                      </label>
                    )}
                    Status
                  </label>
                  <Select
                    placeholder="Status"
                    clearable
                    options={statusOpt}
                    editMode={editMode}
                    module={MODULES.Master}
                    form={FORM}
                    field="scn_status"
                    value={stkConfigData.scnStatus}
                    style={{ height: "20px" }}
                    onChange={(e, data) => {
                      setStkConfigData((prevState) => ({
                        ...prevState,
                        scnStatus: data.value,
                      }));
                    }}
                  />
                  {mFields.scnStatus && mFields.scnStatus.required && (
                    <small style={{ color: "#9f3a38" }}>{`${
                      currentErrors.scnStatus ? currentErrors.scnStatus : ""
                    }`}</small>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentStkConfig).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <Grid
                heading="Stakeholder Configuration"
                config={[
                  {
                    headerName: "Configuration",
                    field: "scnConfigName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Description",
                    field: "scnDescription",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Active/Inactive",
                    field: "scnBoolean",
                  },
                  {
                    headerName: "Number",
                    field: "scnNumber",
                  },
                  { headerName: "UOM", field: "scnUomName" },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        setCurrentStkConfig(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        if (params.data.scnId) {
                          setDeletedList((prevState) => [
                            ...prevState,
                            params.data.scnId,
                          ]);
                        }
                        setStkConfigList((prevConfig) =>
                          prevConfig.filter(
                            (config) => config.id !== params.data.id,
                          ),
                        );
                      },
                       message:
                          "Are you sure you want to delete this Stakeholder Coffiguration?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode:
                        params.data.scnId !== null &&
                        isNumber(params.data.scnId),
                    }),
                  },
                ]}
                content={stkConfigList}
              />
            </div>
          </form>
        )}
      </>
    );
  },
);

export default StakeholderConfig;
